/*
  Sandbox terminal for poster-gallery blocks.

  Whitelisted command set only. The interpreter is pure client-side; no eval,
  no shell execution, no admin / customer / cart / order access. Data source
  is the public store catalogue dumped into a <script type="application/json"
  data-ms-terminal-catalog> block at section render time — same data Shopify
  already exposes publicly to anyone who visits a product or collection page.

  Commands: help, pwd, ls, cd, cat, open, search, echo, whoami, date, clear.

  Each [data-ms-terminal] block initialises its own interpreter; they share
  the catalogue snapshot.
*/
class MsTerminal {
  constructor(el, catalog, options = {}) {
    this.el = el;
    this.history = el.querySelector('[data-history]');
    this.input = el.querySelector('[data-input]');
    this.promptText = el.querySelector('[data-prompt-text]');
    // Capture the initial Liquid-rendered history (ASCII banner + welcome
    // line) BEFORE restoreState replaces it. `clear` falls back to this so
    // the user gets the starting screen, not a blank panel.
    this.welcomeHtml = this.history ? this.history.innerHTML : '';
    this.catalog = catalog;
    this.pwd = '/';
    this.prevPwd = '/';   // remembered for `cd -` (shell-style prev location)
    this.cmdHistory = [];
    this.cmdHistoryIdx = -1;
    this.shopName = (catalog.shop && catalog.shop.name) || 'miscreants';
    this.commands = this.buildCommands();
    // Optional session persistence — when persistKey is set, output history
    // + pwd + command-recall history are saved to sessionStorage on every
    // command and restored on page load. Clears when the tab closes.
    this.persistKey = options.persistKey || null;
    // Page context — set per-render in [data-ms-page-context]. Tells the
    // terminal what page the visitor is currently on, exposes virtual files
    // (data / specs / size for products, etc.) for `ls` and `cat`.
    this.pageContext = null;
    const ctxNode = document.querySelector('[data-ms-page-context]');
    if (ctxNode) {
      try { this.pageContext = JSON.parse(ctxNode.textContent); } catch (e) {}
    }
    this.restoreState();
    this.updatePrompt();
    this.handleNavigation();

    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.handleInput(this.input.value);
        this.input.value = '';
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.recallHistory(-1);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.recallHistory(1);
      } else if (e.key === 'Tab') {
        e.preventDefault();
        this.handleTab();
      } else if (e.key === 'l' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        this.commands.clear();
      }
    });

    // Focus the input when the user clicks anywhere inside the terminal.
    this.el.addEventListener('click', (e) => {
      if (e.target.tagName === 'INPUT') return;
      this.input.focus();
    });
  }

  // ---- output helpers ----
  print(text, opts = {}) {
    const line = document.createElement('div');
    line.className = 'ms-poster__terminal-line';
    if (opts.html) line.innerHTML = text;
    else line.textContent = text;
    this.history.appendChild(line);
    this.scrollToBottom();
  }

  printPrompt(input) {
    const line = document.createElement('div');
    line.className = 'ms-poster__terminal-line ms-poster__terminal-line--prompt';
    line.innerHTML = '<span class="ms-poster__terminal-prompt-text">'
      + this.escape(this.promptText.textContent) + '</span> '
      + this.escape(input);
    this.history.appendChild(line);
  }

  scrollToBottom() {
    this.history.scrollTop = this.history.scrollHeight;
  }

  escape(s) {
    return String(s).replace(/[&<>"]/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;'
    }[c]));
  }

  // ---- input handling ----
  handleInput(input) {
    const trimmed = input.trim();
    this.printPrompt(input);
    if (!trimmed) { this.saveState(); return; }
    this.cmdHistory.push(trimmed);
    this.cmdHistoryIdx = this.cmdHistory.length;

    // Easter eggs — exact-phrase matches that trigger the banner glitch.
    // Whole input is normalised (lowercase, collapsed whitespace) so e.g.
    // "There Is No Spoon" matches "there is no spoon" matches "there  is no spoon".
    const normalised = trimmed.toLowerCase().replace(/\s+/g, ' ');
    const BANNER_GLITCH_PHRASES = [
      'zero cool', 'crash override', 'acid burn',
      'mr robot', 'fsociety', 'hello friend',
      'there is no spoon', 'wake up neo', 'follow the white rabbit',
      "i'm in", 'im in',
      'enhance', 'pwn', 'root', 'sudo',
    ];
    if (BANNER_GLITCH_PHRASES.indexOf(normalised) !== -1) {
      this.glitchBanner();
      this.saveState();
      return;
    }
    if (normalised === 'rm -rf /' || normalised === 'rm -rf') {
      this.pageDissolve();
      this.saveState();
      return;
    }

    const parts = trimmed.split(/\s+/);
    const cmd = parts[0].toLowerCase();
    const args = parts.slice(1);
    const handler = this.commands[cmd];
    if (handler) {
      try { handler.call(this, args); }
      catch (e) { this.print(cmd + ': error — ' + e.message); }
    } else {
      this.print(cmd + ': command not found. Type \'help\'.');
    }
    // Save after every command so state survives a page navigation.
    this.saveState();
  }

  // ---- page navigation awareness ----
  handleNavigation() {
    if (!this.pageContext) return;
    const lastKey = this.persistKey ? this.persistKey + '_last_path' : null;
    let lastPath = null;
    if (lastKey) {
      try { lastPath = sessionStorage.getItem(lastKey); } catch (e) {}
    }
    const currentPath = (window.location && window.location.pathname) || '/';
    if (lastPath === currentPath) return; // same page, no notice

    // Move pwd to the page context's pwd (if provided).
    if (this.pageContext.pwd) {
      this.pwd = this.pageContext.pwd;
      this.updatePrompt();
    }

    // Print a system-style "navigated to..." notice. Distinct prefix («»)
    // separates this from $-prefixed user commands.
    this.print('» navigated to ' + (this.pageContext.pwd || currentPath));
    if (this.pageContext.title) this.print('  ' + this.pageContext.title);
    const vfiles = this.pageContext.virtual_files;
    if (vfiles && Object.keys(vfiles).length) {
      this.print('  cat: ' + Object.keys(vfiles).sort().join(', '));
    }
    this.print('');

    this.saveState();
    if (lastKey) {
      try { sessionStorage.setItem(lastKey, currentPath); } catch (e) {}
    }
  }

  // ---- session persistence ----
  saveState() {
    if (!this.persistKey) return;
    try {
      sessionStorage.setItem(this.persistKey, JSON.stringify({
        historyHtml: this.history.innerHTML,
        pwd: this.pwd,
        cmdHistory: this.cmdHistory.slice(-100), // cap to last 100
      }));
    } catch (e) { /* quota / disabled storage */ }
  }

  restoreState() {
    if (!this.persistKey) return;
    let stored = null;
    try { stored = JSON.parse(sessionStorage.getItem(this.persistKey) || 'null'); } catch (e) {}
    if (!stored) return;
    if (typeof stored.historyHtml === 'string') {
      this.history.innerHTML = stored.historyHtml;
    }
    if (typeof stored.pwd === 'string') {
      this.pwd = stored.pwd;
    }
    if (Array.isArray(stored.cmdHistory)) {
      this.cmdHistory = stored.cmdHistory;
      this.cmdHistoryIdx = this.cmdHistory.length;
    }
    // Scroll to the bottom of the restored history after layout.
    setTimeout(() => this.scrollToBottom(), 0);
  }

  recallHistory(delta) {
    if (this.cmdHistory.length === 0) return;
    this.cmdHistoryIdx = Math.max(0, Math.min(this.cmdHistory.length, this.cmdHistoryIdx + delta));
    this.input.value = this.cmdHistory[this.cmdHistoryIdx] || '';
  }

  // ---- tab completion ----
  handleTab() {
    const raw = this.input.value;
    // Empty input — list all commands (one of bash's tab-tab behaviours).
    if (raw.length === 0) {
      const cmds = Object.keys(this.commands);
      this.printPrompt(raw);
      this.print('  ' + cmds.sort().join('  '));
      return;
    }
    // Did the user type at least one space? If so, complete an argument;
    // otherwise complete the command name.
    const hasSpace = /\s/.test(raw);
    if (!hasSpace) {
      const cmds = Object.keys(this.commands);
      const matches = cmds.filter(c => c.startsWith(raw));
      this.applyCompletion(raw, matches, '');
      return;
    }
    // Completing an argument: take the last whitespace-separated token.
    const parts = raw.split(/\s+/);
    const cmd = parts[0].toLowerCase();
    const argPrefix = parts[parts.length - 1];
    const candidates = this.candidatesForCommand(cmd);
    if (!candidates.length) return;
    const matches = candidates.filter(c => c.startsWith(argPrefix));
    this.applyCompletion(argPrefix, matches, raw.slice(0, raw.length - argPrefix.length));
  }

  applyCompletion(prefix, matches, headBeforePrefix) {
    if (matches.length === 0) return;
    if (matches.length === 1) {
      this.input.value = headBeforePrefix + matches[0] + (headBeforePrefix && !headBeforePrefix.endsWith(' ') ? '' : '');
      return;
    }
    // Multiple matches — try to extend to the common prefix.
    const common = this.commonPrefix(matches);
    if (common.length > prefix.length) {
      this.input.value = headBeforePrefix + common;
      return;
    }
    // No further extension — print the candidates.
    this.printPrompt(this.input.value);
    const capped = matches.slice(0, 30);
    this.print('  ' + capped.join('  '));
    if (matches.length > capped.length) {
      this.print('  ... ' + (matches.length - capped.length) + ' more');
    }
  }

  candidatesForCommand(cmd) {
    if (cmd === 'cat') {
      // When on a page that exposes virtual_files (product / page / article),
      // include those keys so Tab completes `cat title`, `cat text`, etc.
      const ctx = this.pageContext;
      const virtualKeys = (ctx && ctx.pwd === this.pwd && ctx.virtual_files)
        ? Object.keys(ctx.virtual_files) : [];
      return [...virtualKeys, ...this.catalog.products.map(p => p.handle)];
    }
    if (cmd === 'cd') {
      if (this.pwd === '/') return ['collections', 'products', 'journal', 'about', '..', '/'];
      if (this.pwd === '/collections') {
        return this.catalog.collections.map(c => c.handle).concat(['..', '/']);
      }
      if (this.pwd === '/products') {
        return this.catalog.products.map(p => p.handle).concat(['..', '/']);
      }
      if (this.pwd === '/journal') {
        return (this.catalog.articles || []).map(a => a.handle).concat(['..', '/']);
      }
      // Inside a specific collection — complete product handles in that
      // collection so e.g. `cd t<Tab>` from /collections/t-shirts narrows
      // to t-shirts in the t-shirts collection.
      const colMatch = this.pwd.match(/^\/collections\/(.+)$/);
      if (colMatch) {
        const products = (this.catalog.collection_products || {})[colMatch[1]] || [];
        return products.concat(['..', '/']);
      }
      return ['..', '/'];
    }
    if (cmd === 'open') {
      return [
        ...this.catalog.products.map(p => p.handle),
        ...this.catalog.collections.map(c => c.handle),
        ...(this.catalog.articles || []).map(a => a.handle),
      ];
    }
    return [];
  }

  commonPrefix(strings) {
    if (!strings.length) return '';
    let prefix = strings[0];
    for (let i = 1; i < strings.length; i++) {
      while (!strings[i].startsWith(prefix)) {
        prefix = prefix.slice(0, -1);
        if (!prefix) return '';
      }
    }
    return prefix;
  }

  updatePrompt() {
    const shop = (this.shopName || 'shop').toLowerCase().replace(/\s+/g, '-');
    this.promptText.textContent = 'guest@' + shop + ':' + this.pwd + '$';
  }

  // ---- commands ----
  buildCommands() {
    return {
      help: this.cmdHelp,
      pwd: this.cmdPwd,
      ls: this.cmdLs,
      cd: this.cmdCd,
      cat: this.cmdCat,
      open: this.cmdOpen,
      search: this.cmdSearch,
      echo: this.cmdEcho,
      whoami: this.cmdWhoami,
      date: this.cmdDate,
      clear: this.cmdClear,
      hack: this.cmdHack,   // easter egg — intentionally not in help
      '1337': this.cmdHack, // same easter egg, alt invocation
    };
  }

  // Easter egg: typing `hack`, `hack me`, `hack miscreants`, or
  // `hack the planet` scrambles the announcement banner text for ~1s
  // before locking back to the original.
  cmdHack(args) {
    const lower = args.map(s => (s || '').toLowerCase());
    const target = lower[0] || '';
    const isThePlanet = lower[0] === 'the' && lower[1] === 'planet';
    if (target && target !== 'me' && target !== 'miscreants' && !isThePlanet) {
      this.print('hack: unknown target');
      return;
    }
    this.glitchBanner();
  }

  // Shared scramble effect — used by cmdHack and the BANNER_GLITCH_PHRASES
  // easter eggs in handleInput.
  glitchBanner() {
    const items = document.querySelectorAll('.ms-announce__item');
    if (!items.length) {
      this.print('hack: no surface available');
      return;
    }
    this.print('> injecting noise into announcement layer...');
    const CHARS = '!<>?/\\|@#$%^&*=+-_~`░▒▓█01';
    const SCRAMBLE_FRAMES = 14;
    const FRAME_MS = 30;
    items.forEach(el => {
      const original = el.textContent;
      const len = original.length;
      let frame = 0;
      const id = setInterval(() => {
        if (frame < SCRAMBLE_FRAMES) {
          el.textContent = original.split('').map(c =>
            /\s/.test(c) ? c : CHARS[Math.floor(Math.random() * CHARS.length)]
          ).join('');
        } else {
          const lockedCount = frame - SCRAMBLE_FRAMES;
          if (lockedCount >= len) {
            el.textContent = original;
            clearInterval(id);
            return;
          }
          el.textContent = original.split('').map((c, i) => {
            if (i < lockedCount || /\s/.test(c)) return c;
            return CHARS[Math.floor(Math.random() * CHARS.length)];
          }).join('');
        }
        frame++;
      }, FRAME_MS);
    });
  }

  // Easter egg: `rm -rf /` triggers a BSOD overlay that auto-clears after
  // ~5s, returning the visitor to the page. Dramatic, single full-screen
  // animation — no per-section work.
  pageDissolve() {
    if (document.querySelector('.ms-bsod')) return; // already showing
    this.print('> rm: WARNING — recursive system halt issued');
    const bsod = document.createElement('div');
    bsod.className = 'ms-bsod';
    bsod.innerHTML =
      '<div class="ms-bsod__inner">' +
        '<div class="ms-bsod__face">:(</div>' +
        '<div class="ms-bsod__msg">Your store ran into a problem and needs to restart.</div>' +
        '<div class="ms-bsod__sub">We\'re just collecting some error info, then we\'ll restart for you.</div>' +
        '<div class="ms-bsod__progress"><span data-pct>0</span>% complete</div>' +
        '<div class="ms-bsod__details">For more information about this issue and possible fixes, type <code>help</code> in the terminal.<br>Stop code: <code>RM_RF_SLASH</code></div>' +
        '<div class="ms-bsod__restart">Restarting in <span data-cd>5</span>...</div>' +
      '</div>';
    document.body.appendChild(bsod);
    const pctEl = bsod.querySelector('[data-pct]');
    const cdEl = bsod.querySelector('[data-cd]');

    // Fake progress — random nudges, clamped at 99 until the end
    let pct = 0;
    const pctTick = setInterval(() => {
      pct += Math.random() * 8 + 2;
      if (pct > 99) pct = 99;
      pctEl.textContent = Math.floor(pct);
    }, 200);

    // Countdown 5 → 0
    let cd = 5;
    const cdTick = setInterval(() => {
      cd--;
      cdEl.textContent = String(Math.max(0, cd));
      if (cd <= 0) clearInterval(cdTick);
    }, 1000);

    // After 4.5s, finish progress to 100
    setTimeout(() => { clearInterval(pctTick); pctEl.textContent = '100'; }, 4500);
    // After 5s, fade out
    setTimeout(() => { bsod.classList.add('ms-bsod--fading'); }, 5000);
    setTimeout(() => { bsod.remove(); }, 5600);
  }

  cmdHelp() {
    this.print('Available commands:');
    this.print('  help               this list');
    this.print('  pwd                current directory');
    this.print('  ls                 list contents of pwd');
    this.print('  cd <name>          change directory');
    this.print('                     ..  go up one level (and navigate)');
    this.print('                     /   go home');
    this.print('                     -   go back to previous pwd');
    this.print('  cat <handle>       show product details or virtual file');
    this.print('  open <handle>      navigate to a product, collection or article');
    this.print('  search <query>     search product titles');
    this.print('  echo <text>        print text');
    this.print('  whoami             show current user');
    this.print('  date               show current date / time');
    this.print('  clear              clear terminal');
    this.print('');
    this.print('Navigable paths:');
    this.print('  /collections/<handle>   /products/<handle>   /journal/<post>');
    this.print('  /pages/<handle>         /about               /journal');
    this.print('');
    this.print('Tab completes commands + arguments. ArrowUp / ArrowDown recall history.');
  }

  cmdPwd() {
    this.print(this.pwd);
  }

  cmdLs(args) {
    // Page-context virtual files: when the terminal's pwd matches the
    // current page's virtual path (e.g. /products/<handle>), `ls` returns
    // the keys of the virtual_files map (data, specs, size, etc).
    const ctx = this.pageContext;
    if (ctx && ctx.pwd === this.pwd && ctx.virtual_files) {
      const keys = Object.keys(ctx.virtual_files);
      if (keys.length) {
        // For product / page / article pages we list virtual files instead
        // of catalog children. Collection pages keep listing products via
        // the catalog branch below (collection_products), so virtual_files
        // there are accessed via `cat <key>` rather than `ls`.
        if (this.pwd.indexOf('/products/') === 0 ||
            this.pwd.indexOf('/pages/')    === 0 ||
            this.pwd.indexOf('/journal/')  === 0) {
          keys.sort().forEach(k => this.print(k));
          return;
        }
      }
    }

    // Show contents based on pwd.
    if (this.pwd === '/') {
      this.print('collections/  products/  journal/  about/');
      return;
    }
    if (this.pwd === '/collections') {
      const list = this.catalog.collections.map(c => c.handle).sort();
      if (!list.length) { this.print('(empty)'); return; }
      list.forEach(h => this.print(h));
      return;
    }
    if (this.pwd === '/products') {
      const list = this.catalog.products.map(p => p.handle).sort();
      if (!list.length) { this.print('(empty)'); return; }
      list.forEach(h => this.print(h));
      return;
    }
    if (this.pwd === '/journal') {
      const list = (this.catalog.articles || []).map(a => a.handle).sort();
      if (!list.length) { this.print('(no journal posts yet)'); return; }
      list.forEach(h => this.print(h));
      return;
    }
    const colMatch = this.pwd.match(/^\/collections\/(.+)$/);
    if (colMatch) {
      const products = (this.catalog.collection_products || {})[colMatch[1]] || [];
      if (!products.length) { this.print('(empty)'); return; }
      products.slice().sort().forEach(h => this.print(h));
      return;
    }
    this.print('(nothing to list here)');
  }

  // Map a virtual pwd to its real navigable URL. Used by `cd ..` and `cd -`
  // so going "up" or "back" actually moves the browser, not just the prompt.
  urlForPwd(pwd) {
    if (pwd === '/') return '/';
    if (pwd === '/collections') return '/collections';
    if (pwd === '/products')    return '/collections/all';
    if (pwd === '/journal')     return '/blogs/journal';
    if (pwd === '/about')       return '/pages/about';
    if (/^\/collections\/[^/]+$/.test(pwd)) return pwd;
    if (/^\/products\/[^/]+$/.test(pwd))    return pwd;
    if (/^\/pages\/[^/]+$/.test(pwd))       return pwd;
    const artMatch = pwd.match(/^\/journal\/(.+)$/);
    if (artMatch) {
      const article = (this.catalog.articles || []).find(a => a.handle === artMatch[1]);
      return article ? article.url : '/blogs/journal';
    }
    return null;
  }

  // Navigate to a URL if (a) we have a URL, (b) we aren't already there.
  // Otherwise just update pwd silently.
  navigateOrSync(targetPwd) {
    const url = this.urlForPwd(targetPwd);
    if (url && typeof window !== 'undefined' && window.location &&
        window.location.pathname !== url) {
      window.location.href = url;
      return;
    }
    this.pwd = targetPwd;
    this.updatePrompt();
    this.saveState();
  }

  cmdCd(args) {
    const target = (args[0] || '').trim();
    if (!target || target === '~' || target === '/') {
      // `cd /` (and bare `cd` / `cd ~`) returns to the home page. If the
      // visitor is already on the home page we just sync the local pwd
      // without forcing a navigation.
      this.prevPwd = this.pwd;
      this.navigateOrSync('/');
      return;
    }
    if (target === '..') {
      if (this.pwd === '/') return;  // already at root
      const parent = this.pwd.split('/').slice(0, -1).join('/') || '/';
      this.prevPwd = this.pwd;
      this.navigateOrSync(parent);
      return;
    }
    if (target === '-') {
      // shell-style: swap to previously-visited pwd
      const swap = this.prevPwd || '/';
      this.prevPwd = this.pwd;
      this.navigateOrSync(swap);
      return;
    }

    // Relative cd from inside a specific collection — treat the target as
    // a product handle in that collection and jump to the PDP. Matches the
    // tab completion behaviour: `ls` in /collections/t-shirts lists product
    // handles, so `cd <handle>` should open the matching product.
    if (!target.startsWith('/')) {
      const colMatch = this.pwd.match(/^\/collections\/(.+)$/);
      if (colMatch) {
        const products = (this.catalog.collection_products || {})[colMatch[1]] || [];
        if (products.indexOf(target) !== -1) {
          const product = this.catalog.products.find(p => p.handle === target);
          if (product) {
            this.prevPwd = this.pwd;
            window.location.href = product.url;
            return;
          }
        }
      }
    }

    // Absolute or relative.
    let next;
    if (target.startsWith('/')) next = target;
    else if (this.pwd === '/') next = '/' + target;
    else next = this.pwd + '/' + target;
    // Normalise trailing slash.
    if (next.length > 1 && next.endsWith('/')) next = next.slice(0, -1);
    // Validate.
    if (next === '/collections' || next === '/products') {
      this.pwd = next; this.updatePrompt(); return;
    }
    const colMatch = next.match(/^\/collections\/(.+)$/);
    if (colMatch) {
      if (this.catalog.collections.find(c => c.handle === colMatch[1])) {
        // Navigate to the collection page. If we're already on it,
        // just sync the local pwd without forcing a reload.
        const onCollection = (typeof window !== 'undefined') &&
          window.location && window.location.pathname === next;
        if (onCollection) {
          this.pwd = next; this.updatePrompt(); this.saveState(); return;
        }
        window.location.href = next;
        return;
      }
    }
    // /products/<handle> — accept any handle that exists in the catalog
    const prodMatch = next.match(/^\/products\/(.+)$/);
    if (prodMatch) {
      if (this.catalog.products.find(p => p.handle === prodMatch[1])) {
        const onProduct = (typeof window !== 'undefined') &&
          window.location && window.location.pathname === next;
        if (onProduct) {
          this.pwd = next; this.updatePrompt(); this.saveState(); return;
        }
        window.location.href = next;
        return;
      }
    }
    // /pages/<handle> — Shopify static pages, accept any (we don't validate)
    if (/^\/pages\/[^/]+$/.test(next)) {
      const onPage = (typeof window !== 'undefined') &&
        window.location && window.location.pathname === next;
      if (onPage) {
        this.pwd = next; this.updatePrompt(); this.saveState(); return;
      }
      window.location.href = next;
      return;
    }
    // /journal/<article-handle> — aesthetic short path; navigates to the
    // real /blogs/journal/<handle> URL via the article's url field.
    const artMatch = next.match(/^\/journal\/(.+)$/);
    if (artMatch) {
      const article = (this.catalog.articles || []).find(a => a.handle === artMatch[1]);
      if (article) {
        const onArticle = (typeof window !== 'undefined') &&
          window.location && window.location.pathname === article.url;
        if (onArticle) {
          this.pwd = next; this.updatePrompt(); this.saveState(); return;
        }
        window.location.href = article.url;
        return;
      }
    }
    // Top-level static-page shortcuts (aesthetic — terminal shows the
    // short path but resolves to /pages/<handle> or /blogs/<handle> behind
    // the scenes). Add new entries here when more shortcuts are wanted.
    const STATIC_SHORTCUTS = {
      '/journal': '/blogs/journal',
      '/about':   '/pages/about',
    };
    if (STATIC_SHORTCUTS[next]) {
      const resolved = STATIC_SHORTCUTS[next];
      const onShortcut = (typeof window !== 'undefined') &&
        window.location && window.location.pathname === resolved;
      if (onShortcut) {
        this.pwd = next; this.updatePrompt(); this.saveState(); return;
      }
      window.location.href = resolved;
      return;
    }
    this.print('cd: ' + target + ': no such directory');
  }

  cmdCat(args) {
    const target = (args[0] || '').trim();
    if (!target) { this.print('cat: missing operand'); return; }
    // Page-context virtual files — when in /products/<handle> etc. the
    // terminal can read "files" like data / specs / size / price.
    const ctx = this.pageContext;
    if (ctx && ctx.pwd === this.pwd && ctx.virtual_files && (target in ctx.virtual_files)) {
      const val = String(ctx.virtual_files[target]);
      val.split('\n').forEach(line => this.print(line));
      return;
    }
    // Fallback: look up a product by handle from the catalog.
    const product = this.catalog.products.find(p => p.handle === target);
    if (!product) {
      this.print('cat: ' + target + ': no such file or product');
      return;
    }
    this.print(product.title);
    this.print(product.price + '   ' + (product.available ? 'in stock' : 'sold out'));
    this.print('');
    this.print(product.description || '(no description)');
  }

  cmdOpen(args) {
    const target = (args[0] || '').trim();
    if (!target) { this.print('open: missing operand'); return; }
    const product = this.catalog.products.find(p => p.handle === target);
    if (product) { window.location.href = product.url; return; }
    const col = this.catalog.collections.find(c => c.handle === target);
    if (col) { window.location.href = col.url; return; }
    const article = (this.catalog.articles || []).find(a => a.handle === target);
    if (article) { window.location.href = article.url; return; }
    this.print('open: ' + target + ': not found');
  }

  cmdSearch(args) {
    const q = args.join(' ').trim();
    if (!q) { this.print('search: missing query'); return; }
    const lower = q.toLowerCase();
    const matches = this.catalog.products.filter(p =>
      p.title.toLowerCase().includes(lower)
      || (p.description || '').toLowerCase().includes(lower)
    );
    if (!matches.length) {
      this.print('(no matches for "' + q + '")');
      return;
    }
    matches.slice(0, 12).forEach(p => this.print(p.handle.padEnd(28) + ' ' + p.title));
    if (matches.length > 12) {
      this.print('... and ' + (matches.length - 12) + ' more');
    }
  }

  cmdEcho(args) { this.print(args.join(' ')); }
  cmdWhoami() { this.print('guest'); }
  cmdDate() { this.print(new Date().toString()); }
  cmdClear() {
    // Restore the starting welcome state (MISCREANTS banner + tip line)
    // rather than blanking the panel entirely.
    this.history.innerHTML = this.welcomeHtml || '';
    this.scrollToBottom();
  }
}

// Expose MsTerminal globally so other scripts (e.g. ms-terminal-window.js)
// can re-init or reference it.
window.MsTerminal = MsTerminal;

(() => {
  let catalog = { shop: {}, collections: [], products: [], collection_products: {} };
  const catalogNode = document.querySelector('[data-ms-terminal-catalog]');
  if (catalogNode) {
    try { catalog = JSON.parse(catalogNode.textContent); } catch (e) { /* keep defaults */ }
  }
  document.querySelectorAll('[data-ms-terminal]').forEach(el => {
    // Persistence is opt-in via data-ms-persist-key on the element.
    // The floating terminal window uses 'ms_floating_terminal'; inline
    // poster-gallery terminals omit this and stay ephemeral per page.
    const persistKey = el.dataset.msPersistKey || null;
    new MsTerminal(el, catalog, { persistKey });
  });
})();
